import React, { useState, useEffect, useMemo } from 'react';
import { Filter, Mail, ChevronLeft, ChevronRight, Users, Check, RefreshCw } from 'lucide-react';
import referralAnalyticsService, {
    FunnelStatsResponse,
    FunnelAgentsResponse,
    FunnelAgent,
    FunnelFilters,
} from '../services/referral-analytics.service';

// Funnel step colors
const FUNNEL_COLORS = [
    '#3b82f6', // blue - Total Invites
    '#8b5cf6', // purple - Opened Invites
    '#06b6d4', // cyan - Registered
    '#84cc16', // lime - Docs Signed
    '#f59e0b', // amber - Training Done
    '#10b981', // emerald - Ready to Sell
    '#14b8a6', // teal - First Sale
    '#22c55e', // green - First Paycheck
];

interface Campaign {
    id: string;
    name: string;
}

// Trapezoid Funnel Visualization Component
const FunnelVisualization: React.FC<{
    data: FunnelStatsResponse['funnel'];
    selectedStep: number | null;
    onStepClick: (step: number) => void;
}> = ({ data, selectedStep, onStepClick }) => {
    const totalWidth = 600;
    const stepHeight = 50;
    const gap = 4;
    const minWidth = 120;
    const maxWidth = totalWidth;

    return (
        <div className="flex flex-col items-center py-6">
            <svg
                viewBox={`0 0 ${totalWidth} ${data.length * (stepHeight + gap)}`}
                className="w-full max-w-2xl"
                preserveAspectRatio="xMidYMid meet"
            >
                {data.map((step, index) => {
                    // Calculate trapezoid widths
                    const topWidthRatio = 1 - (index / data.length) * 0.7;
                    const bottomWidthRatio = 1 - ((index + 1) / data.length) * 0.7;
                    const topWidth = minWidth + (maxWidth - minWidth) * topWidthRatio;
                    const bottomWidth = minWidth + (maxWidth - minWidth) * bottomWidthRatio;

                    const centerX = totalWidth / 2;
                    const y = index * (stepHeight + gap);

                    // Create trapezoid path
                    const topLeft = centerX - topWidth / 2;
                    const topRight = centerX + topWidth / 2;
                    const bottomLeft = centerX - bottomWidth / 2;
                    const bottomRight = centerX + bottomWidth / 2;

                    const path = `M ${topLeft} ${y} L ${topRight} ${y} L ${bottomRight} ${y + stepHeight} L ${bottomLeft} ${y + stepHeight} Z`;

                    const isSelected = selectedStep === step.step;

                    return (
                        <g
                            key={step.step}
                            onClick={() => onStepClick(step.step)}
                            className="cursor-pointer transition-opacity hover:opacity-90"
                        >
                            {/* Trapezoid shape */}
                            <path
                                d={path}
                                fill={step.color}
                                stroke={isSelected ? '#1f2937' : 'transparent'}
                                strokeWidth={isSelected ? 3 : 0}
                                className="transition-all duration-200"
                            />
                            {/* Step label and count */}
                            <text
                                x={centerX}
                                y={y + stepHeight / 2}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="white"
                                fontSize="14"
                                fontWeight="600"
                                className="pointer-events-none"
                            >
                                {step.name}
                            </text>
                            <text
                                x={centerX}
                                y={y + stepHeight / 2 + 16}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="white"
                                fontSize="12"
                                opacity="0.9"
                                className="pointer-events-none"
                            >
                                {step.count.toLocaleString()} ({step.percentage}%)
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// Filters Component
const FunnelFiltersComponent: React.FC<{
    filters: FunnelFilters;
    onFiltersChange: (filters: FunnelFilters) => void;
    campaigns: Campaign[];
    states: string[];
}> = ({ filters, onFiltersChange, campaigns, states }) => {
    const steps = [
        { value: 1, label: '1. Total Invites' },
        { value: 2, label: '2. Opened Invites' },
        { value: 3, label: '3. Registered' },
        { value: 4, label: '4. Docs Signed' },
        { value: 5, label: '5. Training Done' },
        { value: 6, label: '6. Ready to Sell' },
        { value: 7, label: '7. First Sale' },
        { value: 8, label: '8. First Paycheck' },
    ];

    return (
        <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg mb-6">
            <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            {/* State Filter */}
            <select
                value={filters.state || ''}
                onChange={(e) => onFiltersChange({ ...filters, state: e.target.value || undefined })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="">All States</option>
                {states.map((state) => (
                    <option key={state} value={state}>{state}</option>
                ))}
            </select>

            {/* Campaign Filter */}
            <select
                value={filters.campaignId || ''}
                onChange={(e) => onFiltersChange({ ...filters, campaignId: e.target.value || undefined })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="">All Campaigns</option>
                {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                ))}
            </select>

            {/* Date From */}
            <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value || undefined })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="From Date"
            />

            {/* Date To */}
            <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value || undefined })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="To Date"
            />

            {/* Step Filter */}
            <select
                value={filters.step || ''}
                onChange={(e) => onFiltersChange({ ...filters, step: e.target.value ? parseInt(e.target.value) : undefined })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="">All Steps</option>
                {steps.map((step) => (
                    <option key={step.value} value={step.value}>{step.label}</option>
                ))}
            </select>
        </div>
    );
};

// Agent List Component
const AgentList: React.FC<{
    agents: FunnelAgentsResponse;
    selectedAgents: Set<string>;
    onSelectAgent: (agentId: string) => void;
    onSelectAll: () => void;
    onPageChange: (page: number) => void;
    isLoading: boolean;
}> = ({ agents, selectedAgents, onSelectAgent, onSelectAll, onPageChange, isLoading }) => {
    const allSelected = agents.agents.length > 0 && agents.agents.every(a => selectedAgents.has(a.id));

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading agents...</span>
            </div>
        );
    }

    if (agents.agents.length === 0) {
        return (
            <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No agents found for the selected filters</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={onSelectAll}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Phone
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Step
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invited At
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Campaign
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {agents.agents.map((agent) => (
                        <tr key={agent.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                                <input
                                    type="checkbox"
                                    checked={selectedAgents.has(agent.id)}
                                    onChange={() => onSelectAgent(agent.id)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                        {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <span className="ml-3 text-sm font-medium text-gray-900">{agent.name}</span>
                                </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {agent.email}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {agent.phone || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                    className="px-2 py-1 text-xs font-medium rounded-full text-white"
                                    style={{ backgroundColor: FUNNEL_COLORS[agent.currentStep - 1] || '#6b7280' }}
                                >
                                    {agent.stepName}
                                </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {agent.invitedAt ? new Date(agent.invitedAt).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {agent.campaignName || '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                    Showing {((agents.pagination.page - 1) * agents.pagination.limit) + 1} to{' '}
                    {Math.min(agents.pagination.page * agents.pagination.limit, agents.pagination.total)} of{' '}
                    {agents.pagination.total} results
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange(agents.pagination.page - 1)}
                        disabled={agents.pagination.page <= 1}
                        className="p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {agents.pagination.page} of {agents.pagination.totalPages}
                    </span>
                    <button
                        onClick={() => onPageChange(agents.pagination.page + 1)}
                        disabled={agents.pagination.page >= agents.pagination.totalPages}
                        className="p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main OnboardingFunnel Component
export const OnboardingFunnel: React.FC = () => {
    const [funnelStats, setFunnelStats] = useState<FunnelStatsResponse | null>(null);
    const [agentsData, setAgentsData] = useState<FunnelAgentsResponse | null>(null);
    const [filters, setFilters] = useState<FunnelFilters>({});
    const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    // Mock data for campaigns and states - in a real app, fetch these from API
    const campaigns: Campaign[] = useMemo(() => [
        { id: 'campaign1', name: 'Campaign 1' },
        { id: 'campaign2', name: 'Campaign 2' },
    ], []);

    const states: string[] = useMemo(() => [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    ], []);

    // Fetch funnel stats
    const fetchFunnelStats = async () => {
        try {
            setIsLoadingStats(true);
            setError('');
            const stats = await referralAnalyticsService.getFunnelStats({
                state: filters.state,
                campaignId: filters.campaignId,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
            });
            setFunnelStats(stats);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load funnel statistics');
        } finally {
            setIsLoadingStats(false);
        }
    };

    // Fetch agents
    const fetchAgents = async () => {
        try {
            setIsLoadingAgents(true);
            const agents = await referralAnalyticsService.getFunnelAgents(filters);
            setAgentsData(agents);
            setSelectedAgents(new Set());
        } catch (err: any) {
            console.error('Failed to load agents:', err);
        } finally {
            setIsLoadingAgents(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchFunnelStats();
    }, []);

    // Refetch when filters change
    useEffect(() => {
        fetchFunnelStats();
        fetchAgents();
    }, [filters.state, filters.campaignId, filters.dateFrom, filters.dateTo]);

    // Fetch agents when step filter changes
    useEffect(() => {
        fetchAgents();
    }, [filters.step, filters.page]);

    const handleStepClick = (step: number) => {
        setFilters(prev => ({
            ...prev,
            step: prev.step === step ? undefined : step,
            page: 1,
        }));
    };

    const handleSelectAgent = (agentId: string) => {
        setSelectedAgents(prev => {
            const next = new Set(prev);
            if (next.has(agentId)) {
                next.delete(agentId);
            } else {
                next.add(agentId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (!agentsData) return;

        const allSelected = agentsData.agents.every(a => selectedAgents.has(a.id));
        if (allSelected) {
            setSelectedAgents(new Set());
        } else {
            setSelectedAgents(new Set(agentsData.agents.map(a => a.id)));
        }
    };

    const handlePageChange = (page: number) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handleSendReminder = async () => {
        if (selectedAgents.size === 0) {
            setError('Please select at least one agent to send a reminder');
            return;
        }

        try {
            setIsSendingReminder(true);
            setError('');
            setSuccessMessage('');

            const result = await referralAnalyticsService.sendReminder(Array.from(selectedAgents));

            setSuccessMessage(`Successfully sent ${result.sentCount} reminder email(s)`);
            setSelectedAgents(new Set());

            // Clear success message after 5 seconds
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to send reminder emails');
        } finally {
            setIsSendingReminder(false);
        }
    };

    if (isLoadingStats && !funnelStats) {
        return (
            <div className="bg-white rounded-lg shadow p-8">
                <div className="flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading onboarding funnel...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Onboarding Funnel</h3>
                        <p className="text-sm text-gray-600">
                            Track the 8-step onboarding journey from invite to first paycheck
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            fetchFunnelStats();
                            fetchAgents();
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>

                {/* Filters */}
                <FunnelFiltersComponent
                    filters={filters}
                    onFiltersChange={setFilters}
                    campaigns={campaigns}
                    states={states}
                />

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Success Message */}
                {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <p className="text-green-800">{successMessage}</p>
                    </div>
                )}

                {/* Funnel Visualization */}
                {funnelStats && (
                    <FunnelVisualization
                        data={funnelStats.funnel}
                        selectedStep={filters.step || null}
                        onStepClick={handleStepClick}
                    />
                )}

                {/* Conversion Rates */}
                {funnelStats && (
                    <div className="grid grid-cols-7 gap-2 mt-4 text-center">
                        {Object.entries(funnelStats.conversionRates).map(([key, value], index) => (
                            <div key={key} className="bg-gray-50 rounded p-2">
                                <p className="text-xs text-gray-500">Step {index + 1} → {index + 2}</p>
                                <p className="text-lg font-semibold text-gray-800">{value}%</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Agent List Section */}
            <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">
                        Agents {filters.step ? `at Step ${filters.step}` : '(Select a funnel step to filter)'}
                    </h4>
                    <button
                        onClick={handleSendReminder}
                        disabled={selectedAgents.size === 0 || isSendingReminder}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSendingReminder ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <Mail className="w-4 h-4" />
                        )}
                        Send Reminder ({selectedAgents.size})
                    </button>
                </div>

                {agentsData && (
                    <AgentList
                        agents={agentsData}
                        selectedAgents={selectedAgents}
                        onSelectAgent={handleSelectAgent}
                        onSelectAll={handleSelectAll}
                        onPageChange={handlePageChange}
                        isLoading={isLoadingAgents}
                    />
                )}
            </div>
        </div>
    );
};

export default OnboardingFunnel;
